// "Call on this computer" in the Mac app: this Mac's own sound (the app's
// Core Audio tap, no share picker: native/desktopCallAudio.ts) mixed with the
// microphone through Web Audio into ONE audio track, which the engine records
// like any other (same MediaRecorder, takes, upload and recovery). The browser
// path (callSound.ts, a share) is a separate implementation left as it is; this
// one keeps its rules with a native side in place of a share.
//
// Called straight from the Start tap: the AudioContext is made before anything
// is awaited (so it runs), the app is asked for the Mac's sound first, and the
// microphone joins the mix when it is allowed ("asking_mic" until then). Either
// side may go while recording: the Mac's sound gone carries on with the
// microphone (and can be asked for again from a tap: reshare), the microphone
// gone or muted carries on with the Mac's sound; both gone end the track,
// which the engine reads as a recording cut short. While a new native side
// opens (`opening`), the old one ending (the app keeps one session) never ends
// a call-only take. The mix meters itself (`meter`), so the engine never opens
// a second AudioContext on this stream. Nothing native is connected to
// ctx.destination: it is recorded, never played aloud, so the tap never hears
// the recording back. Stopping the track stops every input, tells the app to
// stop, and closes the context.

import { CALL_MAC_DENIED, startNativeCallAudio, type CallAudioEndWhy, type CallAudioLink } from "@/native/desktopCallAudio";
import { pcmFeed, type PcmFeed } from "./nativePcmFeed";
import { QUIET_LEVEL } from "./recorderModel";
import type { CallSoundLike, StreamLike } from "./recorderTypes";
import { CALL_SILENT_SEC, type CallSoundState } from "./sourceModel";

/** The microphone for the native call path. One place: if the call gets quieter while recording, this is the one line to change (echoCancellation: false). */
export const NATIVE_CALL_MIC = { echoCancellation: true, noiseSuppression: true, autoGainControl: true } as const;

export interface NativeCallDeps { newContext(): AudioContext; open(onPush: (pos: number, pcm: Int16Array, rate: number) => void): Promise<CallAudioLink>; mic(c: MediaTrackConstraints): Promise<MediaStream> }

const browserNativeDeps: NativeCallDeps = {
  newContext: () => new AudioContext(),
  open: (onPush) => startNativeCallAudio(onPush),
  mic: (c) => navigator.mediaDevices.getUserMedia({ audio: c }),
};

type Level = { loudAt: () => number; stop: () => void };
type Side = { gain: GainNode; feed: PcmFeed; level: Level; link: CallAudioLink };

/** One reading of an analyser, 0..1 (recorderMedia.meter's formula). */
function read(an: AnalyserNode, buf: Uint8Array<ArrayBuffer>): number {
  an.getByteTimeDomainData(buf);
  let sum = 0;
  for (let i = 0; i < buf.length; i++) {
    const v = (buf[i] - 128) / 128;
    sum += v * v;
  }
  return Math.min(1, Math.sqrt(sum / buf.length) * 4);
}

/** A side's level, read ten times a second: when it was last loud (callSound.ts's levelOf). */
function levelOf(ctx: AudioContext, src: AudioNode): Level {
  let loud = Date.now();
  try {
    const an = ctx.createAnalyser();
    an.fftSize = 512;
    src.connect(an);
    const buf = new Uint8Array(an.fftSize);
    const t = window.setInterval(() => { if (read(an, buf) >= QUIET_LEVEL) loud = Date.now(); }, 100);
    return { loudAt: () => loud, stop: () => window.clearInterval(t) };
  } catch {
    return { loudAt: () => Date.now(), stop: () => undefined }; // no meter: never warned about
  }
}

export async function captureNativeCallSound(deps: NativeCallDeps = browserNativeDeps): Promise<StreamLike> {
  // Made inside the tap, before any await, so the page lets it run.
  const ctx = deps.newContext();
  const dest = ctx.createMediaStreamDestination();
  const track = dest.stream.getAudioTracks()[0];
  const nativeStop = track.stop.bind(track);
  const mixMeter = ctx.createAnalyser();
  mixMeter.fftSize = 512;
  const sides: Side[] = [];
  const meters = new Set<number>();
  let mic: MediaStream | null = null;
  let micLevel: Level | null = null;
  let callLive = false;
  let micAsking = true;
  let micLive = false;
  let micMuted = false;
  let trackMuted = false;
  /** New native sides being opened (reshare; a counter, so a double tap never clears it under the other). */
  let opening = 0;
  let closed = false;
  let lastEnd: CallAudioEndWhy | null = null;
  const listeners: Array<() => void> = [];
  const liveSide = () => sides.some((s) => !s.link.ended());

  const dropSide = (s: Side) => {
    s.level.stop();
    s.feed.stop();
    if (!s.link.ended()) s.link.stop();
    try { s.gain.disconnect(); } catch { /* already */ }
    const i = sides.indexOf(s);
    if (i >= 0) sides.splice(i, 1);
  };

  const cleanup = () => {
    if (closed) return;
    closed = true;
    for (const s of sides.slice()) dropSide(s);
    micLevel?.stop();
    mic?.getTracks().forEach((t) => t.stop());
    for (const t of meters) window.clearInterval(t);
    meters.clear();
    void ctx.close().catch(() => undefined);
  };
  /** Both sides gone: end the track the way a lost microphone ends (the engine offers Carry on). */
  const endAll = () => {
    if (closed) return;
    nativeStop();
    cleanup();
    track.dispatchEvent(new Event("ended"));
  };
  /** After any change: both gone ends the track (never while a new side opens); the microphone muted while it is the only side mutes it. */
  const changed = () => {
    if (closed) return;
    if (!callLive && !micLive && !micAsking && opening === 0) return endAll();
    const mute = !callLive && micLive && micMuted;
    if (mute !== trackMuted) {
      trackMuted = mute;
      track.dispatchEvent(new Event(mute ? "mute" : "unmute"));
    }
    listeners.forEach((fn) => fn());
  };

  /** A native side: the app asked for this Mac's sound, played into its own gain (to the mix and the meter, never ctx.destination). */
  const openSide = async (): Promise<Side> => {
    const gain = ctx.createGain();
    const feed = pcmFeed(ctx, gain);
    let link: CallAudioLink;
    try {
      link = await deps.open((pos, pcm, rate) => feed.push(pos, pcm, rate));
    } catch (e) {
      feed.stop();
      throw e;
    }
    gain.connect(dest);
    gain.connect(mixMeter);
    const side: Side = { gain, feed, level: levelOf(ctx, gain), link };
    link.onEnded((why) => {
      if (closed) return;
      lastEnd = why;
      side.level.stop();
      if (opening > 0) return;
      callLive = liveSide();
      changed();
    });
    return side;
  };

  let first: Side;
  try {
    first = await openSide();
  } catch (e) {
    void ctx.close().catch(() => undefined);
    throw e;
  }
  void ctx.resume().catch(() => undefined);
  sides.push(first);
  callLive = !first.link.ended();
  // The engine stops the recorded track on Stop: everything behind it goes with it.
  Object.defineProperty(track, "stop", { configurable: true, value: () => { nativeStop(); cleanup(); } });

  // The microphone joins when allowed; this Mac's sound records meanwhile.
  void deps.mic(NATIVE_CALL_MIC)
    .then((m) => {
      micAsking = false;
      if (closed) {
        m.getTracks().forEach((t) => t.stop());
        return;
      }
      mic = m;
      const tracks = m.getAudioTracks();
      if (tracks.length) {
        const src = ctx.createMediaStreamSource(m);
        src.connect(dest);
        src.connect(mixMeter);
        micLevel = levelOf(ctx, src);
        micLive = tracks.some((t) => t.readyState === "live");
        micMuted = tracks.every((t) => t.muted);
        for (const t of tracks) {
          t.addEventListener("ended", () => {
            if (closed || !micLive) return;
            micLive = false;
            changed();
          });
          t.addEventListener("mute", () => { micMuted = true; changed(); });
          t.addEventListener("unmute", () => { micMuted = false; changed(); });
        }
      }
      changed();
    })
    .catch(() => {
      micAsking = false; // this Mac's sound alone still records (said on screen)
      changed();
    });

  const callSound: CallSoundLike = {
    via: "native",
    state: (): CallSoundState => {
      const micOn = micLive && !micMuted;
      if (callLive && micAsking) return "asking_mic";
      return callLive && micOn ? "both" : callLive ? "call_only" : "mic_only";
    },
    onChange: (fn) => void listeners.push(fn),
    lost: () => (!liveSide() && lastEnd === "denied" ? "denied" : null),
    meter(onLevel) {
      try {
        const buf = new Uint8Array(mixMeter.fftSize);
        const t = window.setInterval(() => onLevel(read(mixMeter, buf)), 100);
        meters.add(t);
        return () => { window.clearInterval(t); meters.delete(t); };
      } catch {
        return () => undefined; // the meter is cosmetic
      }
    },
    callSilent: () => {
      if (!callLive || !micLive || micMuted || !micLevel) return false;
      const now = Date.now();
      const callLoud = Math.max(0, ...sides.filter((s) => !s.link.ended()).map((s) => s.level.loudAt()));
      // this Mac quiet for a minute while the microphone heard someone in that minute
      return now - callLoud >= CALL_SILENT_SEC * 1000 && micLevel.loudAt() > callLoud && now - micLevel.loudAt() < CALL_SILENT_SEC * 1000;
    },
    async reshare() {
      if (closed) return "cancelled";
      opening++;
      const before = sides.slice();
      try {
        const side = await openSide();
        if (closed) {
          opening--;
          dropSide(side);
          return "cancelled";
        }
        sides.push(side);
        // The new side replaces any still running (the app keeps one session anyway).
        for (const old of before) dropSide(old);
        opening--;
        callLive = liveSide();
        changed();
        return "ok";
      } catch (e) {
        opening--;
        if (closed) return "cancelled";
        // the old side may have been ended by the app's one-session rule, or may still run
        callLive = liveSide();
        changed();
        return (e as Error).name === CALL_MAC_DENIED ? "blocked" : "cancelled";
      }
    },
  };
  return Object.assign(dest.stream, { callSound }) as unknown as StreamLike;
}
